import * as Sentry from '@sentry/react';
import { AUTH_COOKIE_NAME, getIntraAuthCookie, getIntraAuthCookies } from '@repo/zolintra-utils';
import { isNil } from 'lodash';

const COOKIE_ATTRIBUTES = 'Path=/; Secure; SameSite=Lax';
const EXPIRED_DATE = 'Thu, 01 Jan 1970 00:00:00 GMT';

/**
 * @returns {Object}
 */
export function getStoredUser() {
  try {
    return JSON.parse(window.localStorage.getItem('user')) || {};
  } catch {
    window.localStorage.removeItem('user');

    return {};
  }
}

/**
 * Every parent domain the auth cookie may have been written on.
 *
 * A cookie can only be deleted by rewriting it with the exact same domain, so
 * clearing the current host is not enough: previous versions of the app scoped
 * the cookie to `.${hostname}`, which leaks to every sibling environment
 * (a cookie set by `staff.zol.fr` is also visible from `staging.staff.zol.fr`).
 * The leading dot is ignored by browsers, so the bare form covers both.
 *
 * @returns {Array<string>}
 */
function getInheritedCookieDomains() {
  const labels = window.location.hostname.split('.');

  return labels
    .map((_, index) => labels.slice(index).join('.'))
    .filter((domain) => domain.includes('.'));
}

/**
 * Two cookies sharing the same name make the stored value ambiguous, so drop
 * every domain-scoped copy and keep only the host-only cookie.
 *
 * @returns {void}
 */
function clearInheritedAuthCookies() {
  getInheritedCookieDomains().forEach((domain) => {
    document.cookie = `${AUTH_COOKIE_NAME}=; Domain=${domain}; ${COOKIE_ATTRIBUTES}; Expires=${EXPIRED_DATE}`;
  });
}

/**
 * @returns {{ access_token: string, refresh_token: string }|undefined}
 */
export function getAuthFromCookie() {
  const cookie = getIntraAuthCookie();

  if (!cookie) {
    return undefined;
  }

  try {
    return JSON.parse(cookie);
  } catch {
    return undefined;
  }
}

/**
 * @param {{ access_token: string, refresh_token: string }} auth
 * @returns {void}
 */
export function setAuthCookie(auth) {
  clearInheritedAuthCookies();

  const cookie = JSON.stringify(auth);

  document.cookie = `${AUTH_COOKIE_NAME}=${cookie}; ${COOKIE_ATTRIBUTES}`;

  // Le navigateur ignore silencieusement une écriture de cookie hors limite de
  // taille, ce qui se traduit plus tard par des appels API sans en-tête
  // Authorization : on relève l'anomalie ici plutôt que de la laisser muette.
  const storedCookies = getIntraAuthCookies();

  if (storedCookies.length !== 1) {
    Sentry.captureException(new Error(`Expected a single ${AUTH_COOKIE_NAME} cookie, found ${storedCookies.length}.`));
  }
}

export function clearAuthCookie() {
  document.cookie = `${AUTH_COOKIE_NAME}=; ${COOKIE_ATTRIBUTES}; Expires=${EXPIRED_DATE}`;
  clearInheritedAuthCookies();
  window.localStorage.removeItem('user');
}

/**
 * @returns {boolean}
 */
export function isUserAuthenticated() {
  const connectedUser = getStoredUser();
  const auth = getAuthFromCookie();

  return !!auth?.access_token && connectedUser?.isActive && !isNil(connectedUser?.id);
}

/**
 * @returns {boolean}
 */
export function shouldRedirectToCognitoLogin() {
  const params = new URLSearchParams(window.location.search);

  if (params.get('code')) {
    return false;
  }

  return !isUserAuthenticated();
}

/**
 * @returns {string}
 */
export function getCognitoAuthorizeUrl() {
  const redirectUri = `https://${window.location.hostname}/login`;
  const params = new URLSearchParams({
    response_type: 'code',
    client_id: import.meta.env.VITE_COGNITO_CLIENT_ID,
    redirect_uri: redirectUri,
    identity_provider: 'Google',
  });

  return `${import.meta.env.VITE_COGNITO_URL}/authorize?${params.toString()}`;
}

export function getCognitoLogoutUrl() {
  const redirectUri = `https://${window.location.hostname}/login`;
  const params = new URLSearchParams({
    client_id: import.meta.env.VITE_COGNITO_CLIENT_ID,
    redirect_uri: redirectUri,
    response_type: 'code',
    scope: 'openid',
  });

  return `${import.meta.env.VITE_COGNITO_URL}/logout?${params.toString()}`;
}

export function redirectToCognitoLogin() {
  window.location.href = getCognitoAuthorizeUrl();
}

export function redirectToCognitoLogout() {
  window.location.href = getCognitoLogoutUrl();
}

/**
 * Parses a JWT token to extract the payload as a JSON object.
 *
 * @param {string} token
 * @returns {Object}
 */
export function parseJwt(token) {
  const base64Url = token.split('.')[1];
  const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
  const jsonPayload = decodeURIComponent(window.atob(base64).split('').map((c) => `%${(`00${c.charCodeAt(0).toString(16)}`).slice(-2)}`).join(''));

  return JSON.parse(jsonPayload);
}
